import React, {
    useContext,
    useEffect,
    useState,
  } from "react";
  import axios from "axios";
  import { Link as RouterLink } from "react-router-dom";
  import { toast } from "react-hot-toast";
  
  import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
  } from "@mui/material";
  
  import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
  import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
  import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
  import AppleIcon from "@mui/icons-material/Apple";
  import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
  import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
  
  import { UserContext } from "../../context/userContext/userContext";
  import { useStyle } from "../../context/styleContext/styleContext";
  
  import LoginRegisterModal from "../../components/LoginRegisterModal/LoginRegisterModal";
  import Seo from "../../components/Seo";
  
  export default function RedeemPage() {
    const { user } = useContext(UserContext);
    const { style } = useStyle();
  
    const [code, setCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] =
      useState(false);
    const [redemptionResult, setRedemptionResult] = useState(null);
  
    useEffect(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
  
      /*
       * Supports links such as:
       * https://parishaudio.com/redeem?code=KVR-ABCD-...
       */
      const queryParameters = new URLSearchParams(
        window.location.search
      );
  
      const suppliedCode = queryParameters.get("code");
  
      if (suppliedCode) {
        setCode(suppliedCode.toUpperCase());
      }
    }, []);
  
    const closeLoginModal = () => {
      setLoginRegisterModalOpen(false);
    };
  
    const submitRedemption = async () => {
      const trimmedCode = code.trim();
  
      if (!trimmedCode) {
        toast.error("Please enter a redemption code.");
        return;
      }
  
      const token = localStorage.getItem("token");
  
      if (!token) {
        setLoginRegisterModalOpen(true);
        return;
      }
  
      setIsSubmitting(true);
  
      try {
        const response = await axios.post(
          "/api/redemption-codes/redeem",
          {
            token,
            code: trimmedCode,
          }
        );
  
        setRedemptionResult(response.data);
  
        toast.success(
          response.data.message ||
            "Your plugin has been added to your account."
        );
      } catch (error) {
        const message =
          error.response?.data?.error ||
          "The code could not be redeemed at this time.";
  
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    };
  
    const handleSubmit = async (event) => {
      event.preventDefault();
  
      if (!user && !localStorage.getItem("token")) {
        if (!code.trim()) {
          toast.error("Please enter a redemption code.");
          return;
        }
  
        setLoginRegisterModalOpen(true);
        return;
      }
  
      await submitRedemption();
    };
  
    const loginCallback = async () => {
      setLoginRegisterModalOpen(false);
  
      /*
       * LoginRegisterModal stores the token before calling this callback,
       * so we can immediately continue the pending redemption.
       */
      await submitRedemption();
    };
  
    const handleCodeChange = (event) => {
      setCode(event.target.value.toUpperCase());
  
      if (redemptionResult) {
        setRedemptionResult(null);
      }
    };
  
    const panelSx = {
      borderRadius: "16px",
      backgroundColor: style.darkPanel,
      border: "1px solid rgba(255,255,255,0.08)",
      boxShadow: "none",
    };
  
    return (
      <>
        <Seo
          title="Redeem Plugin Code | Parish Audio"
          description="Redeem a Parish Audio product code and add your plugin to your account."
          canonical="https://parishaudio.com/redeem"
        />
  
        <Box
          sx={{
            width: "100%",
            minHeight: "calc(100vh - 130px)",
            backgroundColor: "black",
            px: {
              xs: 1.25,
              sm: 2,
              md: 3,
            },
            py: {
              xs: 3,
              md: 6,
            },
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 760,
              mx: "auto",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
              }}
            >
              <Box
                sx={{
                  p: {
                    xs: 1.5,
                    sm: 2,
                    md: 2.5,
                  },
                }}
              >
                <Stack spacing={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...panelSx,
                      p: {
                        xs: 2,
                        md: 2.5,
                      },
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Chip
                        label="PRODUCT REDEMPTION"
                        size="small"
                        icon={
                          <RedeemRoundedIcon
                            sx={{
                              color: `${style.color4} !important`,
                            }}
                          />
                        }
                        sx={{
                          alignSelf: "flex-start",
                          color: style.color4,
                          backgroundColor:
                            "rgba(255,255,255,0.06)",
                          border:
                            "1px solid rgba(255,255,255,0.10)",
                          fontWeight: 800,
                        }}
                      />
  
                      <Typography
                        variant="h1"
                        sx={{
                          color: style.color4,
                          fontSize: {
                            xs: "2rem",
                            md: "2.7rem",
                          },
                          lineHeight: 1.05,
                          fontWeight: 900,
                          letterSpacing: "-0.03em",
                        }}
                      >
                        Redeem your plugin
                      </Typography>
  
                      <Typography
                        sx={{
                          color: style.color3,
                          fontSize: "1rem",
                          lineHeight: 1.7,
                        }}
                      >
                        Enter your code, your product will be added to your account.
                      </Typography>
                    </Stack>
                  </Paper>
  
                  {!redemptionResult ? (
                    <Paper
                      component="form"
                      onSubmit={handleSubmit}
                      elevation={0}
                      sx={{
                        ...panelSx,
                        p: {
                          xs: 2,
                          md: 2.5,
                        },
                      }}
                    >
                      <Stack spacing={1.75}>
                        <Box>
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 800,
                              mb: 0.75,
                            }}
                          >
                            Redemption code
                          </Typography>
  
                          <TextField
                            value={code}
                            onChange={handleCodeChange}
                            placeholder="ABC-DEFG-HIJK-LMNO-PQRS"
                            autoComplete="off"
                            autoCapitalize="characters"
                            fullWidth
                            disabled={isSubmitting}
                            inputProps={{
                              maxLength: 80,
                              spellCheck: false,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                color: style.color4,
                                backgroundColor:
                                  style.darkerPanel,
                                borderRadius: "7px",
  
                                "& fieldset": {
                                  borderColor:
                                    "rgba(255,255,255,0.12)",
                                },
  
                                "&:hover fieldset": {
                                  borderColor: style.color3,
                                },
  
                                "&.Mui-focused fieldset": {
                                  borderColor: style.color2,
                                },
                              },
  
                              "& input": {
                                fontFamily: "monospace",
                                fontSize: {
                                  xs: "0.96rem",
                                  sm: "1.08rem",
                                },
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                              },
                            }}
                          />
                        </Box>
  
                        <Button
                          type="submit"
                          fullWidth
                          disabled={
                            isSubmitting || !code.trim()
                          }
                          startIcon={
                            isSubmitting ? (
                              <CircularProgress
                                size={18}
                                sx={{
                                  color: "inherit",
                                }}
                              />
                            ) : (
                              <RedeemRoundedIcon />
                            )
                          }
                          sx={{
                            minHeight: 50,
                            borderRadius: "5px",
                            textTransform: "none",
                            fontWeight: 800,
                            color: style.color4,
                            backgroundColor: style.accentGreen,
  
                            "&:hover": {
                              backgroundColor:
                                style.accentGreen,
                              filter: "brightness(1.05)",
                            },
  
                            "&.Mui-disabled": {
                              color:
                                "rgba(255,255,255,0.45)",
                              backgroundColor:
                                "rgba(255,255,255,0.08)",
                            },
                          }}
                        >
                          {isSubmitting
                            ? "Redeeming..."
                            : "Redeem Plugin"}
                        </Button>
  
                        <Divider
                          sx={{
                            backgroundColor:
                              "rgba(255,255,255,0.08)",
                          }}
                        />
  
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <LockOutlinedIcon
                            sx={{
                              color: style.color3,
                              fontSize: "1.1rem",
                              mt: "2px",
                            }}
                          />
  
                          <Typography
                            sx={{
                              color: style.color3,
                              lineHeight: 1.55,
                              fontSize: "0.9rem",
                            }}
                          >
                            You will be asked to sign in or create a
                            free Parish Audio account before the code
                            is redeemed.
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        ...panelSx,
                        p: {
                          xs: 2,
                          md: 2.5,
                        },
                      }}
                    >
                      <Stack spacing={2}>
                        <Alert
                          severity="success"
                          icon={<CheckCircleRoundedIcon />}
                          sx={{
                            backgroundColor:
                              "rgba(20,170,70,0.15)",
                            color: style.color4,
                            border:
                              "1px solid rgba(30,200,90,0.30)",
  
                            "& .MuiAlert-icon": {
                              color: "rgb(30,200,90)",
                            },
                          }}
                        >
                          {redemptionResult.message}
                        </Alert>
  
                        <Box>
                          <Typography
                            sx={{
                              color: style.color3,
                              fontSize: "0.88rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                            }}
                          >
                            Product added
                          </Typography>
  
                          <Typography
                            sx={{
                              mt: 0.35,
                              color: style.color4,
                              fontSize: "1.6rem",
                              fontWeight: 900,
                            }}
                          >
                            {redemptionResult.product?.name}
                          </Typography>
                        </Box>
  
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.65,
                          }}
                        >
                          Install the plugin, open it in your DAW,
                          and sign in using your Parish Audio email
                          address and password to unlock it.
                        </Typography>
  
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          spacing={1}
                        >
                          {redemptionResult.product
                            ?.windowsDownloadURI && (
                            <Button
                              component="a"
                              href={
                                redemptionResult.product
                                  .windowsDownloadURI
                              }
                              startIcon={
                                <WindowsRoundedIcon />
                              }
                              sx={{
                                flex: 1,
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor:
                                  "rgba(255,255,255,0.05)",
                                border:
                                  "1px solid rgba(255,255,255,0.10)",
  
                                "&:hover": {
                                  backgroundColor:
                                    "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Download for Windows
                            </Button>
                          )}
  
                          {redemptionResult.product
                            ?.macDownloadURI && (
                            <Button
                              component="a"
                              href={
                                redemptionResult.product
                                  .macDownloadURI
                              }
                              startIcon={<AppleIcon />}
                              sx={{
                                flex: 1,
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor:
                                  "rgba(255,255,255,0.05)",
                                border:
                                  "1px solid rgba(255,255,255,0.10)",
  
                                "&:hover": {
                                  backgroundColor:
                                    "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Download for Mac
                            </Button>
                          )}
                        </Stack>
  
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          spacing={1}
                        >
                          {redemptionResult.product
                            ?.productURI && (
                            <Button
                              component={RouterLink}
                              to={`/products/${redemptionResult.product.productURI}`}
                              endIcon={
                                <ArrowForwardRoundedIcon />
                              }
                              sx={{
                                color: style.color4,
                                textTransform: "none",
                                fontWeight: 700,
                              }}
                            >
                              View product page
                            </Button>
                          )}
  
                          <Button
                            onClick={() => {
                              setCode("");
                              setRedemptionResult(null);
                            }}
                            sx={{
                              color: style.color3,
                              textTransform: "none",
                              fontWeight: 700,
                            }}
                          >
                            Redeem another code
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Box>
  
        <LoginRegisterModal
          isOpen={isLoginRegisterModalOpen}
          close={closeLoginModal}
          loginComplete={loginCallback}
        />
      </>
    );
  }